// Copyright (c) StrangeBee 2023

import { useCallback, useEffect, useState } from 'react';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { THForm } from '@Molecules/THForm/THForm';
import { Space } from 'antd';
import { THMarkdown } from '@Molecules/Inputs/THMarkdown/THMarkdown';
import { useLocalMessage } from '@Helpers/locale';
import { ReportTemplateWidgetEnum, ReportTemplateWidgetText } from 'thehive-sdk';
import useValidateForm from '@Hooks/useValidateForm';
import { VariableSelector } from './VariableSelector';
import { BaseWidgetFormProps } from '../FormInterface';

import './TextForm.scss';
import { THInput } from '@Molecules/Inputs/THInput/THInput';

interface TextFormValues {
    template: string;
}

interface TextFormProps extends BaseWidgetFormProps<ReportTemplateWidgetText> {
    disableVariableSelector?: boolean;
    notRequired?: boolean;
    noTitle?: boolean;
}

export const TextForm = ({
    onFinish,
    defaultValues,
    disableVariableSelector = false,
    notRequired = false,
    onCancel,
    noTitle = false,
}: TextFormProps) => {
    const i = useLocalMessage();
    const { form, onFormValuesChange, isValid } = useValidateForm();
    const [titleLength, setTitleLength] = useState<number>(0);

    const [cursorSelection, setCursorSelection] = useState<{ start: number | null; end: number | null }>({
        start: null,
        end: null,
    });

    useEffect(() => {
        form.setFieldsValue(defaultValues);
    }, [defaultValues, form]);

    const handleFinish = useCallback(
        (values: TextFormValues) => {
            onFinish({ ...values, template: values.template || '', _kind: ReportTemplateWidgetEnum.Text });
        },
        [onFinish],
    );

    return (
        <THForm<TextFormValues>
            form={form}
            name="report-text-form"
            onFinish={handleFinish}
            initialValues={defaultValues}
            onValuesChange={onFormValuesChange}
        >
            <THForm.Layout
                footer={
                    <FormSubmitButton
                        disabled={notRequired ? false : !isValid}
                        onCancel={() => {
                            form.resetFields();
                            onCancel();
                        }}
                    />
                }
            >
                {!noTitle && (
                    <THForm.Item
                        name="title"
                        label={i('common.title')}
                        rules={[
                            {
                                max: 512,
                                message: i('report-template.widgets.title.error', {
                                    curr: titleLength.toString(),
                                }),
                            },
                        ]}
                    >
                        <THInput onChange={(e) => setTitleLength(e.target.value.length)} />
                    </THForm.Item>
                )}
                <THForm.Item<any> noStyle shouldUpdate>
                    {({ getFieldValue, setFieldsValue }) => (
                        <THForm.Item
                            name="template"
                            label={i('report-template.text.form.template.label')}
                            extraAction={
                                !disableVariableSelector && (
                                    <Space>
                                        <VariableSelector
                                            cursor={cursorSelection}
                                            onSelect={(parsedTemplate: string) => {
                                                setFieldsValue({ template: parsedTemplate });
                                                onFormValuesChange();
                                            }}
                                            parentFieldValue={getFieldValue('template') as string}
                                        />
                                    </Space>
                                )
                            }
                            rules={notRequired ? [] : [{ required: true }]}
                        >
                            <THMarkdown
                                sameSize
                                className="text-form__markdown"
                                onBlur={(focusEvent) => {
                                    setCursorSelection({
                                        start: focusEvent.target.selectionStart,
                                        end: focusEvent.target.selectionEnd,
                                    });
                                }}
                            />
                        </THForm.Item>
                    )}
                </THForm.Item>
            </THForm.Layout>
        </THForm>
    );
};
